import { useMemo } from 'react'
import 'katex/dist/katex.min.css'
import type { QuestionResponse } from '../types'
import { renderLatexContent, processImagePlaceholders } from '../utils/renderContent'

interface QuestionViewerProps {
  question: QuestionResponse
  questionNumber: number
  totalQuestions: number
  showSolution?: boolean
}

const OPTION_LABELS = ['A', 'B', 'C', 'D']

/**
 * Converts testCode to a friendly display name
 * e.g., NEET_2024 -> NEET 2024, NEET_2024_RE -> NEET 2024 ReExam
 */
function formatTestName(testCode: string): string {
  if (!testCode) return ''

  // Handle special cases
  if (testCode.includes('_RE')) {
    const year = testCode.match(/\d{4}/)?.[0] || ''
    return `NEET ${year} ReExam`
  }

  if (testCode.includes('_PH_1')) {
    const year = testCode.match(/\d{4}/)?.[0] || ''
    return `NEET ${year} Phase 1`
  }

  if (testCode.includes('_PH_2')) {
    const year = testCode.match(/\d{4}/)?.[0] || ''
    return `NEET ${year} Phase 2`
  }

  // Default: NEET_2024 -> NEET 2024
  const year = testCode.match(/\d{4}/)?.[0] || ''
  return year ? `NEET ${year}` : testCode
}

export default function QuestionViewer({
  question,
  questionNumber,
  totalQuestions,
  showSolution = false,
}: QuestionViewerProps) {
  const testCode = question.testCode

  const renderedQuestion = useMemo(
    () => renderLatexContent(processImagePlaceholders(question.questionData.text, testCode)),
    [question.questionData.text, testCode]
  )

  const renderedOptions = useMemo(
    () => question.questionData.options.map((opt) =>
      renderLatexContent(processImagePlaceholders(opt, testCode))
    ),
    [question.questionData.options, testCode]
  )

  const renderedExplanation = useMemo(
    () =>
      question.solutionData?.explanation
        ? renderLatexContent(processImagePlaceholders(question.solutionData.explanation, testCode))
        : null,
    [question.solutionData?.explanation, testCode]
  )

  const correctAnswer = question.solutionData?.answer

  const hasWorkedSolution =
    !!renderedExplanation ||
    !!question.solutionData?.steps?.length ||
    !!question.solutionData?.keyConcepts?.length

  // Explanation, steps and key concepts
  const workedSolution = question.solutionData && hasWorkedSolution && (
    <div>
      {/* Explanation */}
      {renderedExplanation && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2">
            Explanation:
          </h4>
          <div
            className="text-gray-700 leading-relaxed bg-blue-50 p-4 rounded-lg"
            dangerouslySetInnerHTML={{ __html: renderedExplanation }}
          />
        </div>
      )}

      {/* Steps */}
      {question.solutionData.steps && question.solutionData.steps.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-600 mb-2">Solution:</h4>
          <div className="space-y-3">
            {question.solutionData.steps.map((step, idx) => (
              <p
                key={idx}
                className="text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: renderLatexContent(processImagePlaceholders(step, testCode)),
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Key Concepts */}
      {question.solutionData.keyConcepts &&
        question.solutionData.keyConcepts.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-600 mb-2">
              Key Concepts:
            </h4>
            <div className="flex flex-wrap gap-2">
              {question.solutionData.keyConcepts.map((concept, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
                >
                  {concept}
                </span>
              ))}
            </div>
          </div>
        )}
    </div>
  )

  const solutionContent = question.solutionData && (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Solution</h3>

      {/* Correct Answer */}
      <div className="mb-4">
        <span className="text-sm font-medium text-gray-600">
          Correct Answer:{' '}
        </span>
        <span className="text-green-600 font-bold">{correctAnswer}</span>
      </div>

      {workedSolution}
    </div>
  )

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Question Info Bar */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600">
            Question {questionNumber} of {totalQuestions}
          </span>
          <div className="flex gap-2">
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                question.difficultyLevel === 'EASY'
                  ? 'bg-green-100 text-green-700'
                  : question.difficultyLevel === 'MEDIUM'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {question.difficultyLevel}
            </span>
            {question.testCode && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                {formatTestName(question.testCode)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Question Content - Scrollable */}
      <div className="relative flex-1 min-h-0 overflow-y-auto p-4">
        {/* Question Text */}
        <div
          className="text-gray-800 text-base leading-relaxed mb-6"
          dangerouslySetInnerHTML={{ __html: renderedQuestion }}
        />

        {/* Options */}
        <div className="space-y-3">
          {renderedOptions.map((optionHtml, idx) => {
            const isCorrect = showSolution && correctAnswer === OPTION_LABELS[idx]
            return (
              <div
                key={idx}
                className={`flex items-start p-3 rounded-lg border transition-colors ${
                  isCorrect
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium mr-3 ${
                    isCorrect
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {OPTION_LABELS[idx]}
                </span>
                <div
                  className="flex-1 text-gray-700"
                  dangerouslySetInnerHTML={{ __html: optionHtml }}
                />
              </div>
            )
          })}
        </div>

        {/* Solution Section */}
        {showSolution && solutionContent}
      </div>
    </div>
  )
}
